import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FP610 } from '../../services/pwp254Api';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import PermissionGuard from '../../components/PermissionGuard';
import { usePWP254Mode } from './hooks/usePWP254Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { usePWP254Record } from './hooks/usePWP254Record';
import { usePWP254Navigation } from './hooks/usePWP254Navigation';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Detail from './components/Tab2Detail';
import ActionBar from './components/ActionBar';
import './PWP254Form.css';

const PWP254Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const permissions = useModulePermission(Modules.PWP254);
  const { mode, setMode, isEditing } = usePWP254Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow,
    loadRecord, setField, getField,
    setDetailField, setRateField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = usePWP254Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePWP254Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load PWP254 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // -- Action Handlers --

  const handleAdd = useCallback(() => {
    if (isEditing) {
      // Save
      handleSave(mode, (result: FP610) => {
        clearFieldErrors();
        setMode('browse');
        applyResult(result);
        // Refresh list and position
        loadList().then(ids => {
          const idx = ids.indexOf(result.fp610_01);
          if (idx >= 0) setCurrentIdx(idx);
        });
      });
    } else {
      // Enter add mode
      setMode('add');
      setActiveTab(0);
      clearNames();
      resetForAdd();
    }
  }, [isEditing, mode, handleSave, setMode, clearNames, resetForAdd, applyResult, loadList, setCurrentIdx, clearFieldErrors]);

  const handleModify = useCallback(() => {
    if (isEditing) {
      // Revert
      setMode('browse');
      revertToRecord();
      if (record) resolveAllNames(record);
    } else {
      // Enter edit mode
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, setMode, revertToRecord, resolveAllNames]);

  const handleDeleteClick = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `是否刪除包裝單 ${record.fp610_01}?`,
      okType: 'danger',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const ids = await loadList();
          if (ids.length > 0) {
            const newIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(newIdx);
            await loadRecordWithNames(ids[newIdx]);
          }
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames]);

  const handlePrint = useCallback(() => {
    message.info('列印功能開發中');
  }, []);

  const handleExit = useCallback(() => {
    navigate('/erp');
  }, [navigate]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Tab labels
  const tabs = ['基本資料', '明細'];

  return (
    <PermissionGuard module={Modules.PWP254}>
      <div className="pwp254-form">
        {loading && <div className="loading-overlay">載入中...</div>}

        <div className="page-wrapper">
          {/* Title bar */}
          <div className="page-title-bar">
            <div className="page-title-left">
              <h1>出貨包裝單 (Packing List)</h1>
            </div>
            <div className="page-title-right">
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                操作員: {names.operator || formData.fp610_91 || ''}
              </span>
            </div>
          </div>

          {/* Info bar: doc number + date */}
          <div className="form-info-bar">
            <div className="field-group">
              <label>單據號碼</label>
              <input type="text"
                value={mode === 'add' ? '*新增*' : String(formData.fp610_01 || '')}
                readOnly disabled style={{ width: 120 }} />
            </div>
            <div className="field-group">
              <label>日期</label>
              <input type="text"
                value={formData.fp610_11 ? String(formData.fp610_11).substring(0, 10) : ''}
                readOnly disabled style={{ width: 100 }} />
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <div className="tab-nav">
              {tabs.map((label, i) => (
                <button key={i}
                  className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                  onClick={() => setActiveTab(i)}>
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 0 && (
              <Tab1BasicInfo
                formData={formData}
                names={names}
                isEditing={isEditing}
                getField={getField}
                setField={setField}
                resolveSingleName={resolveSingleName}
              />
            )}

            {activeTab === 1 && (
              <Tab2Detail
                formData={formData}
                details={details}
                selectedRow={selectedRow}
                isEditing={isEditing}
                setSelectedRow={setSelectedRow}
                setDetailField={setDetailField}
                setRateField={setRateField}
                addDetailRow={addDetailRow}
                deleteDetailRow={deleteDetailRow}
              />
            )}
          </div>
        </div>

        {/* Bottom Action Bar */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={permissions}
          onAdd={handleAdd}
          onModify={handleModify}
          onDelete={handleDeleteClick}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={handleExit}
        />
      </div>
    </PermissionGuard>
  );
};

export default PWP254Form;
